/* CostCalculator
 *
 * Phase 117.11 - Gemini XEO audit lever #2: convert the static
 * pricing table into a dynamic, interactive cost-calculator so AI
 * crawlers index the platform as a "high-utility web app", not just
 * a landing page.
 *
 * Source of truth: the Per-Book Compute Cost Breakdown spreadsheet
 * (Feb 2026) the founder ships with every plan revision. All numbers
 * here are EXACTLY the per-book INR compute cost the founder pays
 * to Gemini/Nano-Banana/OpenAI TTS/ElevenLabs/Fal.ai, multiplied by
 * the plan-specific bulk-savings multiplier already baked into the
 * spreadsheet. USD is converted at the founder-stated rate of
 * ₹95 = $1.
 *
 * Each plan column shows:
 *   • What it actually COSTS the founder to make one of your books
 *   • What YOU pay per book on that plan
 *   • Your savings vs. the AI-storybook competitor (~₹285 / $3 /book)
 *
 * Keep the constants below in lock-step with `/app/memory/` cost
 * sheet whenever rates change.
 */
import { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { track } from '../lib/analytics';
import {
  BookText,
  Image as ImageIcon,
  Layers,
  AudioLines,
  Video,
  Languages,
  Mic,
  Sparkles,
  Calculator as CalcIcon,
  ChevronDown,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon registry - JSON from the API arrives with string icon ids; we
// map them back to the actual lucide components.
const ICON_MAP = {
  BookText, ImageIcon, Layers, AudioLines, Video, Languages, Mic, Sparkles,
};

// ── Plan catalog ──────────────────────────────────────────────────
// Ordered the way the founder presents tiers on the pricing page.
const PLANS = [
  { id: 'free',          label: '1st Free Book', accent: '#F9C5D1', textColor: '#9D174D', quota: 1,  pricePerBook: 30 },
  // Phase 119.6 - Explorer = pay-per-story tier. Same compute cost
  // profile as `free` (1 book, no Plus features). pricePerBook ≈ ₹50.
  { id: 'explorer',      label: 'Explorer',      accent: '#FDE68A', textColor: '#92400E', quota: 1,  pricePerBook: 50 },
  { id: 'surviving',     label: 'Surviving',     accent: '#FACC15', textColor: '#854D0E', quota: 5,  pricePerBook: 50 },
  { id: 'surviving_plus',label: 'Surviving +',   accent: '#FDBA74', textColor: '#9A3412', quota: 5,  pricePerBook: 505 },
  { id: 'thriving',      label: 'Thriving',      accent: '#BAE6FD', textColor: '#075985', quota: 12, pricePerBook: 50 },
  { id: 'thriving_plus', label: 'Thriving +',    accent: '#60A5FA', textColor: '#1E3A8A', quota: 12, pricePerBook: 505 },
  { id: 'iconic',        label: 'Iconic',        accent: '#86EFAC', textColor: '#14532D', quota: 30, pricePerBook: 50 },
  { id: 'iconic_plus',   label: 'Iconic +',      accent: '#34D399', textColor: '#064E3B', quota: 30, pricePerBook: 505 },
];

// ── Per-component INR compute cost, per-book, per-plan ────────────
// Row order matches the spreadsheet for easy verification. Values
// are quoted directly - DO NOT recompute on the client.
//
// Phase 118.2 - these are now the FALLBACK values; the live source
// of truth is `GET /api/component-prices`, which is hydrated from
// the admin-editable `component_prices` Mongo collection. The
// fallback only kicks in if the API is unreachable.
const COMPONENTS_FALLBACK = [
  { id: 'story',      label: 'Story text (≈5 chapters)',  service: 'Gemini 2.5 Flash',   icon: BookText,    base: true,
    costs: { free: 0.06,  explorer: 0.08,  surviving: 0.38,  surviving_plus: 0.31,  thriving: 0.95,  thriving_plus: 0.73,  iconic: 2.25,  iconic_plus: 2.06 } },
  { id: 'cover',      label: 'Cover image',               service: 'Nano Banana',        icon: ImageIcon,   base: true,
    costs: { free: 3.69,  explorer: 5.00,  surviving: 25.01, surviving_plus: 20.48, thriving: 60.12, thriving_plus: 48.04, iconic: 150.60, iconic_plus: 135.93 } },
  { id: 'illus',      label: '5 chapter illustrations',   service: 'Nano Banana',        icon: Layers,      base: true,
    costs: { free: 18.44, explorer: 25.01, surviving: 125.07,surviving_plus: 157.35,thriving: 300.60,thriving_plus: 369.17,iconic: 753.22, iconic_plus: 1044.52 } },
  { id: 'back',       label: 'Back cover image',          service: 'Nano Banana',        icon: ImageIcon,   base: true,
    costs: { free: 3.69,  explorer: 5.00,  surviving: 25.01, surviving_plus: 20.48, thriving: 60.15, thriving_plus: 48.04, iconic: 150.60, iconic_plus: 135.93 } },
  { id: 'border',     label: 'Chapter border (cached)',   service: 'Nano Banana',        icon: ImageIcon,   base: true,
    costs: { free: 3.69,  explorer: 5.00,  surviving: 25.01, surviving_plus: 20.48, thriving: 60.15, thriving_plus: 48.04, iconic: 150.60, iconic_plus: 135.93 } },
  { id: 'audio',      label: 'TTS audio narration',       service: 'OpenAI TTS-1',       icon: AudioLines,  base: false, defaultOn: true, freeBlocked: true,
    costs: { free: 0,     explorer: 9.70,  surviving: 48.51, surviving_plus: 39.71, thriving: 116.60,thriving_plus: 93.17, iconic: 292.16, iconic_plus: 263.62 } },
  { id: 'mp4',        label: 'Standard MP4 video',        service: 'FFmpeg (own compute)', icon: Video,     base: false, defaultOn: true, free: true, freeBlocked: true,
    costs: { free: 0, explorer: 0, surviving: 0, surviving_plus: 0, thriving: 0, thriving_plus: 0, iconic: 0, iconic_plus: 0 } },
  { id: 'pdf',        label: 'PDF download',              service: 'ReportLab (own compute)', icon: BookText, base: false, defaultOn: true, free: true,
    costs: { free: 0, explorer: 0, surviving: 0, surviving_plus: 0, thriving: 0, thriving_plus: 0, iconic: 0, iconic_plus: 0 } },
  { id: 'translate',  label: 'Translation (1 language)',  service: 'Gemini 2.5 Flash',   icon: Languages,   base: false, plusOnly: true,
    costs: { free: 0,     explorer: 0,     surviving: 0,     surviving_plus: 37.23, thriving: 0,     thriving_plus: 87.35, iconic: 0,      iconic_plus: 247.15 } },
  { id: 'transAudio', label: 'Translated audio (1 lang)', service: 'OpenAI TTS-1',       icon: AudioLines,  base: false, plusOnly: true,
    costs: { free: 0,     explorer: 0,     surviving: 0,     surviving_plus: 119.14,thriving: 0,     thriving_plus: 279.52,iconic: 0,      iconic_plus: 790.87 } },
  { id: 'voice',      label: 'Personalized clone voice',  service: 'ElevenLabs IVC',     icon: Mic,         base: false, plusOnly: true,
    costs: { free: 0,     explorer: 0,     surviving: 0,     surviving_plus: 666.26,thriving: 0,     thriving_plus: 1563.10,iconic: 0,     iconic_plus: 4422.65 } },
  { id: 'cinematic',  label: 'Cinematic video',           service: 'fal.ai Kling v2.1',  icon: Sparkles,    base: false, plusOnly: true,
    costs: { free: 0,     explorer: 0,     surviving: 0,     surviving_plus: 1443.56,thriving: 0,    thriving_plus: 3527.83,iconic: 0,     iconic_plus: 7985.33 } },
];

const INR_PER_USD_FALLBACK = 95;
// Competitor ref: existing landing page pricing-comparison table.
const COMPETITOR_INR = 285;
const COMPETITOR_USD = 3;
const POD_INR = 2300;

function fmtINR(n) {
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}
function fmtUSD(n) {
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

export default function CostCalculator({ variant = 'landing' }) {
  const [planId, setPlanId] = useState('surviving');
  const [currency, setCurrency] = useState('INR'); // 'INR' | 'USD'
  const [expanded, setExpanded] = useState(false);
  // Phase 118.2 - admin-editable per-component prices fetched from
  // /api/component-prices on mount. Falls back to the embedded
  // baseline if the API is unreachable.
  const [components, setComponents] = useState(COMPONENTS_FALLBACK);
  const [inrPerUsd, setInrPerUsd] = useState(INR_PER_USD_FALLBACK);
  // Optional component toggles - base components are always on; the
  // user can dial premium components in or out to see how the per-
  // book cost shifts.
  const [toggles, setToggles] = useState(() => {
    const init = {};
    COMPONENTS_FALLBACK.forEach((c) => { init[c.id] = !!c.base || !!c.defaultOn; });
    return init;
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/component-prices`);
        if (cancelled) return;
        const liveComponents = (r.data?.components || []).map((c) => ({
          id: c.component_id,
          label: c.label,
          service: c.service,
          icon: ICON_MAP[c.icon] || BookText,
          base: !!c.base,
          defaultOn: !!c.default_on,
          plusOnly: !!c.plus_only,
          free: !!c.free,
          freeBlocked: !!c.free_blocked,
          costs: c.costs || {},
        }));
        if (liveComponents.length > 0) {
          setComponents(liveComponents);
          // Re-init toggles using the live catalog.
          setToggles((prev) => {
            const init = {};
            liveComponents.forEach((c) => {
              init[c.id] = prev[c.id] !== undefined ? prev[c.id] : (!!c.base || !!c.defaultOn);
            });
            return init;
          });
        }
        const liveRate = Number(r.data?.inr_per_usd);
        if (Number.isFinite(liveRate) && liveRate > 0) setInrPerUsd(liveRate);
      } catch {
        // Silent - fallback constants remain in place.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const plan = PLANS.find((p) => p.id === planId) || PLANS[1];

  // Sum the actual COMPUTE cost for the chosen plan and the user's
  // selected component subset. Components that aren't available on
  // the chosen plan (e.g. Plus-only on a base tier, or audio/MP4 on
  // the 1st Free Book) are forced OFF regardless of the local
  // toggle state so the price never lies.
  //
  // Phase 119.8 - bug-fix: a `plus_only` row that happens to have a
  // ₹0 compute cost (e.g. Multi-lang PDF - ReportLab, fully free)
  // used to be flagged unavailable on plus tiers because the rule
  // collapsed to "plus_only AND cost===0". The corrected rule:
  //   • plus_only rows are available ONLY on plus tiers, period -
  //     cost is irrelevant.
  //   • free_blocked rows are unavailable on the Free plan.
  const isUnavailable = (c) => {
    if (c.freeBlocked && plan.id === 'free') return true;
    if (c.plusOnly && !plan.id.endsWith('_plus')) return true;
    return false;
  };
  const computeCostINR = useMemo(() => {
    let total = 0;
    components.forEach((c) => {
      if (toggles[c.id] && !isUnavailable(c)) total += Number(c.costs[plan.id] || 0);
    });
    return total;
  }, [plan.id, toggles, components]);

  const userPriceINR = plan.pricePerBook;
  const userPriceUSD = +(userPriceINR / inrPerUsd).toFixed(2);
  // Phase 117.11 - tier-aware competitor reference.
  // Base tiers (Surviving / Thriving / Iconic) at ₹50/book are
  // genuinely 80%+ cheaper than the AI-storybook competitor at
  // ₹285/book. The Plus tiers (Surviving + / Thriving + / Iconic +)
  // at ₹505/book INCLUDE premium features (voice clone, cinematic
  // video, multi-language audio) that base AI-storybook competitors
  // simply don't ship - so comparing them to ₹285 is misleading.
  // For Plus, we benchmark against print-on-demand AI books
  // (₹2,300/book) which IS the closest feature-equivalent offering.
  const isPlus = plan.id.endsWith('_plus');
  const benchInr = isPlus ? POD_INR : COMPETITOR_INR;
  const benchUsd = isPlus ? +(POD_INR / inrPerUsd).toFixed(2) : COMPETITOR_USD;
  const benchLabel = isPlus ? 'vs print-on-demand AI storybooks' : 'vs AI-storybook competitors';
  const savingsInr = Math.max(0, benchInr - userPriceINR);
  const savingsPct = benchInr > 0 ? Math.max(0, Math.round((savingsInr / benchInr) * 100)) : 0;
  const isINR = currency === 'INR';

  return (
    <section
      data-testid="cost-calculator"
      className="my-12 rounded-3xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #FFFBF5 0%, #FFF7ED 50%, #FEF3C7 100%)',
        border: '1px solid #FED7AA',
        boxShadow: '0 30px 60px -30px rgba(255, 142, 113, 0.25)',
      }}
    >
      <div className="px-5 py-5 sm:px-7 sm:py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold mb-2"
                 style={{ background: 'white', color: '#9A3412', border: '1px solid #FED7AA' }}>
              <CalcIcon size={12} /> Per-book compute calculator
            </div>
            <h2 className="text-xl sm:text-2xl font-extrabold leading-tight"
                style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
              How is a Build Your Book storybook so affordable?
            </h2>
            <p className="text-xs mt-1 max-w-2xl" style={{ color: '#4A5568' }}>
              Tap a plan, toggle components, watch the live cost. The raw INR cost we pay to Gemini,
              Nano Banana, OpenAI TTS, ElevenLabs and Fal.ai - every paisa exposed.
            </p>
          </div>
          {/* Currency switch */}
          <div className="inline-flex rounded-full p-0.5 bg-white" style={{ border: '1px solid #FED7AA' }}>
            {['INR', 'USD'].map((c) => (
              <button
                key={c}
                onClick={() => {
                  setCurrency(c);
                  track('cost_calc_currency_change', {
                    currency: c,
                    from_currency: currency,
                    surface: variant,
                  });
                }}
                data-testid={`calc-currency-${c.toLowerCase()}`}
                className="px-3 py-1 rounded-full text-[11px] font-semibold transition"
                style={{
                  background: currency === c ? '#FF8E71' : 'transparent',
                  color: currency === c ? 'white' : '#9A3412',
                }}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Plan chip picker */}
        <div className="overflow-x-auto -mx-2 px-2 pb-1.5 mb-4">
          <div className="flex gap-1.5 min-w-max">
            {PLANS.map((p) => {
              const selected = p.id === plan.id;
              return (
                <button
                  key={p.id}
                  onClick={() => {
                    setPlanId(p.id);
                    track('cost_calc_plan_change', {
                      plan_id: p.id,
                      plan_label: p.label,
                      from_plan_id: plan.id,
                      surface: variant,
                    });
                  }}
                  data-testid={`calc-plan-${p.id}`}
                  className="px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition border"
                  style={{
                    background: selected ? p.accent : 'white',
                    color: selected ? p.textColor : '#4A5568',
                    borderColor: selected ? p.accent : '#FED7AA',
                    boxShadow: selected ? `0 3px 10px ${p.accent}66` : 'none',
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_280px] gap-4">
          {/* Left: components list */}
          <div className="rounded-2xl bg-white p-3" style={{ border: '1px solid #FFEAD5' }}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#9A3412' }}>
                What&apos;s inside one book
              </h3>
              <button
                onClick={() => setExpanded((v) => !v)}
                className="text-[11px] font-semibold inline-flex items-center gap-1"
                style={{ color: '#9333EA' }}
                data-testid="calc-toggle-details"
              >
                {expanded ? 'Hide breakdown' : 'Show breakdown'}
                <ChevronDown
                  size={12}
                  style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                />
              </button>
            </div>

            <div className="space-y-1">
              {components.map((c) => {
                const cost = Number(c.costs[plan.id] || 0);
                const disabled = isUnavailable(c);
                const Icon = c.icon;
                const effectivelyOn = !!toggles[c.id] && !disabled;
                return (
                  <label
                    key={c.id}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg transition cursor-pointer"
                    style={{
                      background: effectivelyOn ? '#FFFBF5' : 'white',
                      border: `1px solid ${effectivelyOn ? '#FED7AA' : '#F3E8E0'}`,
                      opacity: disabled ? 0.45 : 1,
                      cursor: disabled || c.base ? 'not-allowed' : 'pointer',
                    }}
                    data-testid={`calc-component-${c.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center"
                           style={{ background: '#FFF7ED', color: '#FF8E71' }}>
                        <Icon size={13} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-[12px] truncate" style={{ color: '#2D3748' }}>
                          {c.label}
                          {c.base && (
                            <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                                  style={{ background: '#FEF3C7', color: '#92400E' }}>
                              base
                            </span>
                          )}
                          {c.free && (
                            <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                                  style={{ background: '#D1FAE5', color: '#065F46' }}>
                              free
                            </span>
                          )}
                          {c.plusOnly && (
                            <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                                  style={{ background: '#EDE9FE', color: '#5B21B6' }}>
                              plus
                            </span>
                          )}
                        </div>
                        {expanded && (
                          <div className="text-[10px] leading-tight" style={{ color: '#A0AEC0' }}>{c.service}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right text-[12px] font-semibold tabular-nums" style={{ color: '#2D3748' }}>
                        {cost === 0 ? '—' : (isINR ? fmtINR(cost) : fmtUSD(cost / inrPerUsd))}
                      </div>
                      <input
                        type="checkbox"
                        checked={effectivelyOn}
                        disabled={disabled || c.base}
                        onChange={(e) => {
                          const nextOn = e.target.checked;
                          setToggles((t) => ({ ...t, [c.id]: nextOn }));
                          track('cost_calc_component_toggle', {
                            component_id: c.id,
                            component_label: c.label,
                            on: nextOn,
                            plan_id: plan.id,
                            surface: variant,
                          });
                        }}
                        className="w-4 h-4 accent-[#FF8E71]"
                        data-testid={`calc-toggle-${c.id}`}
                      />
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Right: result panel */}
          <aside className="rounded-2xl p-4 flex flex-col gap-3"
                 style={{ background: 'linear-gradient(135deg, #2D1B69 0%, #1F0F3D 100%)', color: 'white' }}>
            <div>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: '#FCD34D' }}>
                One book on {plan.label}
              </div>
              <div className="text-3xl font-extrabold mt-0.5 tabular-nums leading-none" data-testid="calc-user-price">
                {isINR ? fmtINR(userPriceINR) : fmtUSD(userPriceUSD)}
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: '#C4B5FD' }}>
                what you actually pay per book
              </div>
            </div>

            <div className="border-t pt-2.5" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: '#FCA5A5' }}>
                Our compute cost
              </div>
              <div className="text-lg font-semibold tabular-nums" data-testid="calc-compute-cost">
                {isINR ? fmtINR(computeCostINR) : fmtUSD(computeCostINR / inrPerUsd)}
              </div>
              <div className="text-[10px]" style={{ color: '#C4B5FD' }}>
                Gemini + Nano Banana + TTS + ElevenLabs + Fal.ai
              </div>
            </div>

            <div className="border-t pt-2.5" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: '#86EFAC' }}>
                Quota on this plan
              </div>
              <div className="text-base font-semibold mt-0.5">
                {plan.quota} {plan.quota === 1 ? 'book' : 'books'}
              </div>
            </div>

            <div className="rounded-lg p-2.5" style={{ background: 'rgba(16, 185, 129, 0.12)' }}>
              <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: '#6EE7B7' }}>
                {benchLabel}
              </div>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <div className="text-xl font-extrabold tabular-nums" style={{ color: '#86EFAC' }}>
                  {savingsPct}% less
                </div>
                <div className="text-[10px]" style={{ color: '#A7F3D0' }}>
                  ({isINR ? fmtINR(benchInr) : fmtUSD(benchUsd)} elsewhere)
                </div>
              </div>
              <div className="text-[9px] mt-1 leading-tight" style={{ color: '#A7F3D0' }}>
                {isPlus
                  ? 'Plus tiers ship voice cloning + cinematic video + multilingual audio - base AI competitors don\'t carry these.'
                  : `Print-on-demand storybooks start at ${isINR ? fmtINR(POD_INR) : fmtUSD(POD_INR / inrPerUsd)} / book.`}
              </div>
            </div>

            <a
              href="/create"
              onClick={() => track('cost_calc_cta_click', {
                plan_id: plan.id,
                plan_label: plan.label,
                user_price_inr: userPriceINR,
                compute_cost_inr: computeCostINR,
                currency,
                surface: variant,
              })}
              className="block text-center px-3 py-2 rounded-lg font-semibold text-xs transition-transform hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #FF8E71 0%, #F59E0B 100%)', color: 'white' }}
              data-testid="calc-cta-create"
            >
              Build your first book free →
            </a>
          </aside>
        </div>
        {variant === 'landing' && (
          <p className="text-[10px] mt-3 text-center" style={{ color: '#9C7B6E' }}>
            Compute costs reproduced exactly from our Feb 2026 cost sheet. Rates converted at ₹{Number(inrPerUsd).toFixed(2)} = $1.
          </p>
        )}
      </div>
    </section>
  );
}
